'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRouter, useSearchParams } from 'next/navigation';
import { Check, ClipboardList, Copy, ExternalLink, Search, X } from 'lucide-react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { toast } from 'sonner';
import {
  ADD_FAVORITE_MUTATION,
  REMOVE_FAVORITE_MUTATION,
  FAVORITES_QUERY,
  DOCUMENT_BY_NUMBER_QUERY,
  DOCUMENT_CHAIN_QUERY,
  type DocumentLine,
  type FavoriteItem,
  type RelatedDocument,
  DocumentRelation,
  DocumentType,
  LineType,
  NetUnitPriceStatus,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import {
  ArticleNameBlock,
  ArticleTitleButton,
  Checkbox,
  FavoriteIcon,
  useNavHighlight,
  HighlightText,
} from '@paeffgen/react/crm-documents/ui';
import { useCart } from '@paeffgen/react/crm-documents/feature-material-requests';
import {
  formatCurrency,
  formatQuantity,
  formatPackageBreakdown,
  groupLinesByDelivery,
  groupLineItems,
  type DeliveryBucket,
} from '@paeffgen/react/crm-documents/util';
import { clsx } from 'clsx';

// Stabile leere Auswahl, damit DesktopDeliveryCard auch bei deaktivierter
// Selektion eine Set-Referenz erhaelt (kein neues Set pro Render).
const EMPTY_SELECTION: Set<string> = new Set();

/**
 * Render the quantity cell with smart-fallback packaging info.
 *
 * Two-tier (default): `30 m²` + `6 × 5 m²/VE` (Anz × MengeProPaket).
 * Three-tier (when both pieces & qty per package set): `40 m` + `1 × 10 × 4 m/VE`
 * (Anz × StkProPaket × MengeProPaket — e.g. 1 Bund × 10 Latten × 4 m/Latte).
 * No sub-text for trivial 1-piece packs ("1 × 1 Stück" looks silly).
 */
function QuantityCell({
  line,
  className = '',
  subClassName = 'text-xs text-gray-500 tabular-nums',
}: {
  line: DocumentLine;
  className?: string;
  subClassName?: string;
}) {
  const sub = formatPackageBreakdown(line);
  return (
    <>
      <span className={className}>
        {line.quantity != null ? formatQuantity(line.quantity) : '—'}{' '}
        <span className="text-gray-600">{line.unit}</span>
      </span>
      {sub && <div className={subClassName}>{sub}</div>}
    </>
  );
}

interface DocumentLinesProps {
  lines: DocumentLine[];
  documentId?: string;
  documentType?: DocumentType;
  netUnitPriceStatus?: NetUnitPriceStatus;
  /** Articlenumber whose row should be highlighted in the positions table. */
  highlightArticleNumber?: string | null;
  /** Positions-Auswahl (Key = lineNumber). Nur wenn gesetzt, rendert die
   *  Desktop-Tabelle Auswahl-Checkboxen — Owner ist DocumentDetailDialog,
   *  damit Aktionsleiste (DocumentDetail) und Tabelle dieselbe Auswahl teilen. */
  selected?: Set<string>;
  setSelected?: (
    next: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => void;
}

interface DesktopDeliveryCardProps {
  bucket: DeliveryBucket;
  renderArticleRow: (line: DocumentLine) => React.ReactNode;
  showPrices: boolean;
  showNettoPe: boolean;
  isLegacyMissing: boolean;
  legacyMissingTitle: string;
  onJumpToOrder: (orderNumber: string) => void;
  /** Order numbers that exist as documents — only these become a clickable link. */
  existingOrderNumbers: Set<string>;
  /** Auswahl aktiv → Lieferungs-Kopf bekommt eine „Alle auswählen"-Checkbox. */
  selectionEnabled: boolean;
  selected: Set<string>;
  onToggleBucket: (bucket: DeliveryBucket) => void;
}

/**
 * Desktop-Variante der Mobile-`MobileDeliveryCard`: statisch (kein Akkordeon-
 * Toggle, alle Cards immer aufgeklappt), Header zeigt Lieferungs-Metadaten,
 * Tabelle darunter rendert die Positionen dieses Buckets.
 */
function DesktopDeliveryCard({
  bucket,
  renderArticleRow,
  showPrices,
  showNettoPe,
  isLegacyMissing,
  legacyMissingTitle,
  onJumpToOrder,
  existingOrderNumbers,
  selectionEnabled,
  selected,
  onToggleBucket,
}: DesktopDeliveryCardProps) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {bucket.deliveryNumber && (
        <div className="flex items-baseline justify-between gap-3 px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-sm font-bold text-gray-900">
                Lieferung {bucket.deliveryNumber}
              </span>
              {bucket.deliveryDate && (
                <span className="text-xs text-gray-500 tabular-nums">
                  Lieferdatum: {bucket.deliveryDate}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-baseline gap-x-2 text-xs text-gray-500 mt-0.5">
              {bucket.orderNumber &&
                (existingOrderNumbers.has(bucket.orderNumber) ? (
                  <button
                    type="button"
                    className="hover:text-primary"
                    onClick={() => onJumpToOrder(bucket.orderNumber!)}
                  >
                    Auftrag {bucket.orderNumber}
                  </button>
                ) : (
                  <span>Auftrag {bucket.orderNumber}</span>
                ))}
              {bucket.orderNumber && bucket.orderDate && (
                <>
                  <span className="text-gray-300">·</span>
                  <span className="tabular-nums">
                    Auftragsdatum: {bucket.orderDate}
                  </span>
                </>
              )}
              {bucket.orderNumber && <span className="text-gray-300">·</span>}
              <span>{bucket.lines.length} Pos.</span>
              {bucket.commission && (
                <>
                  <span className="text-gray-300">·</span>
                  <span className="text-orange-700 font-medium">
                    {bucket.commission}
                  </span>
                </>
              )}
            </div>
          </div>
          <span className="text-sm font-bold text-gray-900 tabular-nums shrink-0">
            {formatCurrency(bucket.sum)}
          </span>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="text-gray-600 font-medium border-b border-gray-200">
            <tr>
              {selectionEnabled && (
                <th className="px-4 py-3 w-10">
                  <Checkbox
                    checked={
                      bucket.lines.length > 0 &&
                      bucket.lines.every((l) => selected.has(l.lineNumber))
                    }
                    onClick={() => onToggleBucket(bucket)}
                  />
                </th>
              )}
              <th className="px-4 py-3 w-40">Art.-Nr.</th>
              <th className="px-4 py-3">Beschreibung</th>
              <th className="px-4 py-3 text-right">Menge</th>
              {showPrices && <th className="px-4 py-3 text-right">Einzelpreis</th>}
              {showPrices && <th className="px-4 py-3 text-right">Rabatt</th>}
              {showNettoPe && (
                <th
                  className="px-4 py-3 text-right"
                  title={isLegacyMissing ? legacyMissingTitle : undefined}
                >
                  NettoPE
                </th>
              )}
              {showPrices && <th className="px-4 py-3 text-right">Gesamt</th>}
              <th className="px-4 py-3 text-center w-48">Aktionen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {groupLineItems(bucket.displayLines).map((item) =>
              item.kind === 'text-group' ? (
                <tr key={item.key} className="bg-gray-50">
                  <td
                    colSpan={
                      (selectionEnabled ? 1 : 0) +
                      4 +
                      (showPrices ? 3 : 0) +
                      (showNettoPe ? 1 : 0)
                    }
                    className="px-4 py-2 text-sm text-gray-600 italic"
                  >
                    {item.texts.map((t, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && (
                          <span className="mx-2 text-gray-400 not-italic">·</span>
                        )}
                        {t}
                      </React.Fragment>
                    ))}
                  </td>
                </tr>
              ) : (
                renderArticleRow(item.line)
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function DocumentLines({
  lines,
  documentId,
  documentType,
  netUnitPriceStatus,
  highlightArticleNumber,
  selected,
  setSelected,
}: DocumentLinesProps) {
  const { addItem, items: materialListItems } = useCart();
  const { markHighlight } = useNavHighlight();
  const { adminQueryVars, adminMutationVars, isSingleCustomerContext } = useAdmin();

  // Kunden-eigener Status (Favoriten-Füllung, Anfrage-Highlight) nur mit genau
  // einem Debitor-Kontext — sonst zeigten die Icons Aggregat-/Fremd-Daten an.
  const cartItems = isSingleCustomerContext ? materialListItems : [];
  const router = useRouter();
  const searchParams = useSearchParams();
  const apollo = useApolloClient();

  // Push `?open=<id>` while preserving existing URL params (filter, sort, etc.)
  // so jumping to a referenced document does not blow away the user's list state.
  const pushOpen = (id: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set('open', id);
    router.push(`/belege?${params.toString()}`, { scroll: false });
  };

  const navigateToReferencedDocument = async (documentNumber: string) => {
    try {
      const result = await apollo.query<{
        documentByNumber: { id: string } | null;
      }>({
        query: DOCUMENT_BY_NUMBER_QUERY,
        variables: { number: documentNumber, ...adminQueryVars },
        fetchPolicy: 'network-only',
      });
      const doc = result.data?.documentByNumber;
      if (doc) {
        pushOpen(doc.id);
      } else {
        toast.warning(`Beleg ${documentNumber} nicht gefunden`);
      }
    } catch {
      toast.error(`Beleg ${documentNumber} konnte nicht geladen werden`);
    }
  };

  const { data: favoritesData } = useQuery<{ favorites: FavoriteItem[] }>(
    FAVORITES_QUERY,
    {
      variables: { ...adminQueryVars },
      fetchPolicy: 'cache-first',
      // Kein Favoriten-Abruf über alle Kunden im Aggregat-Modus — das Herz
      // bliebe ohnehin leer (favoriteArticleNumbers wird dann leer).
      skip: !isSingleCustomerContext,
    }
  );

  const refetchFavs = useMemo(
    () => [{ query: FAVORITES_QUERY, variables: { ...adminQueryVars } }],
    [adminQueryVars]
  );

  const [addFavorite] = useMutation(ADD_FAVORITE_MUTATION, {
    refetchQueries: refetchFavs,
  });

  const [removeFavorite] = useMutation(REMOVE_FAVORITE_MUTATION, {
    refetchQueries: refetchFavs,
  });

  const favoriteArticleNumbers = new Set(
    favoritesData?.favorites?.map((f) => f.articleNumber) ?? []
  );

  const handleToggleFavorite = async (articleNumber: string | null) => {
    if (!articleNumber) return;
    if (favoriteArticleNumbers.has(articleNumber)) {
      await removeFavorite({ variables: { articleNumber, ...adminMutationVars } });
    } else {
      await addFavorite({ variables: { articleNumber, ...adminMutationVars } });
      markHighlight('favoriten');
    }
  };

  // Spiegelt den mobilen Positions-Action-Sheet-Handler (gleiche Toast-Texte),
  // damit Desktop-Nutzer die Art.-Nr. nicht manuell markieren müssen.
  const handleCopyArticleNumber = async (articleNumber: string | null) => {
    if (!articleNumber) return;
    try {
      await navigator.clipboard.writeText(articleNumber);
      toast.success(`${articleNumber} kopiert`);
    } catch {
      toast.error('Kopieren nicht möglich');
    }
  };

  const selectionEnabled = !!selected && !!setSelected;
  const selectionSet = selected ?? EMPTY_SELECTION;

  // Positions-Suche (spiegelt MobilePositionsSection): lokaler Filter über
  // Beschreibung/Art.-Nr.; Suchfeld erst ab >=5 Positionen. Bei aktiver Suche
  // rendert der Desktop die flache Tabelle (Lieferungs-Gruppierung wird
  // übersprungen), analog zum mobilen flatMode.
  const articleLines = useMemo(
    () => lines.filter((l) => l.lineType === LineType.ARTICLE),
    [lines]
  );
  const [positionSearch, setPositionSearch] = useState('');
  const filteredArticles = useMemo(() => {
    const q = positionSearch.trim().toLowerCase();
    if (!q) return null;
    return articleLines.filter(
      (l) =>
        l.description?.toLowerCase().includes(q) ||
        l.description2?.toLowerCase().includes(q) ||
        l.articleNumber?.toLowerCase().includes(q)
    );
  }, [positionSearch, articleLines]);
  const isSearching = filteredArticles != null;
  // Im Suchmodus zeigt der Desktop die gefilterten Artikel; sonst alle.
  const displayedArticles = filteredArticles ?? articleLines;

  // Auswahl-Toggles (nur wirksam, wenn der Aufrufer setSelected liefert).
  const toggleLine = (line: DocumentLine) => {
    setSelected?.((prev) => {
      const next = new Set(prev);
      const k = line.lineNumber;
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };
  const setLinesSelected = (target: DocumentLine[], select: boolean) => {
    setSelected?.((prev) => {
      const next = new Set(prev);
      target.forEach((l) =>
        select ? next.add(l.lineNumber) : next.delete(l.lineNumber)
      );
      return next;
    });
  };
  const toggleBucket = (bucket: DeliveryBucket) => {
    const allOn = bucket.lines.every((l) => selectionSet.has(l.lineNumber));
    setLinesSelected(bucket.lines, !allOn);
  };

  const showPrices = documentType !== DocumentType.LIEFERSCHEIN;
  const showNettoPe =
    showPrices &&
    (netUnitPriceStatus === NetUnitPriceStatus.AVAILABLE ||
      netUnitPriceStatus === NetUnitPriceStatus.LEGACY_MISSING);
  const isLegacyMissing =
    netUnitPriceStatus === NetUnitPriceStatus.LEGACY_MISSING;
  const legacyMissingTitle =
    'Dieses Dokument wurde vor Einführung des Felds importiert.';

  // Art.-Nr. + Beschreibung + Menge + Aktionen = 4 Pflichtspalten
  // + Einzelpreis (showPrices) + Rabatt (showPrices) + Gesamt (showPrices) + NettoPE (showNettoPe)
  const desktopColCount =
    (selectionEnabled ? 1 : 0) + 4 + (showPrices ? 3 : 0) + (showNettoPe ? 1 : 0);

  // Aufeinanderfolgende TEXT-Zeilen werden zu einem Hinweisblock gruppiert
  // (geteilte Logik mit den Lieferungs-Cards, s. groupLineItems).
  const groupedItems = useMemo(() => groupLineItems(lines), [lines]);

  // Lieferungs-Buckets — bei Sammelrechnungen rendern wir auf Desktop pro
  // Lieferung eine eigene Card (statt einer einzigen flachen Tabelle).
  // Einzelbelege haben `hasGrouping === false` und behalten den flachen Pfad.
  const buckets = useMemo(() => groupLinesByDelivery(lines), [lines]);
  const hasGrouping = buckets.some((b) => b.deliveryNumber != null);

  // Chain-basiertes Sprung-Ziel-Lookup für RECHNUNGEN.
  // cache-first → Detail-Komponente lädt die Chain ohnehin schon, hier nur Lesezugriff.
  const isInvoice = documentType === DocumentType.RECHNUNG;
  const { data: chainData } = useQuery<{ documentChain: RelatedDocument[] }>(
    DOCUMENT_CHAIN_QUERY,
    {
      variables: { documentId, ...adminQueryVars },
      fetchPolicy: 'cache-first',
      skip: !isInvoice || !documentId,
    }
  );
  const chainDelivery = useMemo(
    () =>
      chainData?.documentChain?.find(
        (c) =>
          c.relation === DocumentRelation.PREDECESSOR &&
          c.type === DocumentType.LIEFERSCHEIN
      ),
    [chainData]
  );
  const chainOrder = useMemo(
    () =>
      chainData?.documentChain?.find(
        (c) =>
          c.relation === DocumentRelation.PREDECESSOR &&
          c.type === DocumentType.AUFTRAG
      ),
    [chainData]
  );
  // Auftragsnummern, die tatsächlich als Beleg existieren — nur diese bekommen
  // einen klickbaren Link (sonst tote Links, da viele Aufträge nicht importiert
  // sind). Quelle: die ohnehin geladene Beleg-Chain.
  const existingOrderNumbers = useMemo(
    () =>
      new Set(
        (chainData?.documentChain ?? [])
          .filter((c) => c.type === DocumentType.AUFTRAG)
          .map((c) => c.documentNumber)
      ),
    [chainData]
  );

  type JumpTarget = { label: string; action: () => void };
  const buildTargets = (
    line: DocumentLine
  ): { delivery?: JumpTarget; order?: JumpTarget } => {
    if (!isInvoice) return {};
    const delivery: JumpTarget | undefined = line.deliveryNumber
      ? {
          label: `Lieferschein ${line.deliveryNumber}`,
          action: () => navigateToReferencedDocument(line.deliveryNumber!),
        }
      : chainDelivery
        ? {
            label: `Lieferschein ${chainDelivery.documentNumber}`,
            action: () => pushOpen(chainDelivery.id),
          }
        : undefined;
    const order: JumpTarget | undefined = line.orderNumber
      ? {
          label: `Auftrag ${line.orderNumber}`,
          action: () => navigateToReferencedDocument(line.orderNumber!),
        }
      : chainOrder
        ? {
            label: `Auftrag ${chainOrder.documentNumber}`,
            action: () => pushOpen(chainOrder.id),
          }
        : undefined;
    return { delivery, order };
  };

  // Dropdown-State: nur ein Dropdown gleichzeitig offen.
  // Das Dropdown wird per Portal (s. renderJumpButton) an document.body
  // gerendert, damit `overflow-hidden` auf der umgebenden DesktopDeliveryCard
  // (Sammelrechnung) das Menue nicht abschneidet. Anker-Rect haelt die
  // Position des ausloesenden Buttons fuer das fixed-positionierte Menue.
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [dropdownAnchorRect, setDropdownAnchorRect] = useState<DOMRect | null>(
    null
  );
  const closeJumpDropdown = () => {
    setOpenDropdownId(null);
    setDropdownAnchorRect(null);
  };
  useEffect(() => {
    if (!openDropdownId) return;
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      const dropdownRoots = document.querySelectorAll('[data-jump-dropdown]');
      for (const el of Array.from(dropdownRoots)) {
        if (el.contains(target)) return;
      }
      closeJumpDropdown();
    };
    const handleDismiss = () => closeJumpDropdown();
    document.addEventListener('mousedown', handleOutside);
    // Scroll/Resize: Position waere ungueltig — UX-konsistent einfach schliessen.
    window.addEventListener('scroll', handleDismiss, true);
    window.addEventListener('resize', handleDismiss);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('scroll', handleDismiss, true);
      window.removeEventListener('resize', handleDismiss);
    };
  }, [openDropdownId]);

  // Erkennt Belegnummern in TEXT-Zeilen-Inhalten (z. B. „Auftragsnr.: AU0752616")
  // und macht sie klickbar — Sprung über die existierende searchText-Navigation.
  const DOC_NUMBER_REGEX = /\b(?:AU|AN|VL|VRG|VGG|RE|GU)\d+\b/g;
  const linkifyDocumentNumbers = (text: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let key = 0;
    for (const match of text.matchAll(DOC_NUMBER_REGEX)) {
      const start = match.index ?? 0;
      if (start > lastIndex) parts.push(text.slice(lastIndex, start));
      const docNo = match[0];
      parts.push(
        <button
          key={`m${key++}`}
          onClick={() => navigateToReferencedDocument(docNo)}
          className="text-primary hover:underline font-mono not-italic"
        >
          {docNo}
        </button>
      );
      lastIndex = start + docNo.length;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts;
  };

  const renderJumpButton = (
    line: DocumentLine,
    variant: 'desktop' | 'mobile'
  ) => {
    const targets = buildTargets(line);
    const available: JumpTarget[] = [];
    if (targets.delivery) available.push(targets.delivery);
    if (targets.order) available.push(targets.order);
    if (available.length === 0) return null;

    const single = available.length === 1 ? available[0] : null;
    const tooltip = single ? `Zum ${single.label}` : 'Zu Lieferschein/Auftrag';
    const isOpen = openDropdownId === line.lineNumber;
    const iconSize = variant === 'mobile' ? 20 : 18;
    const buttonClass =
      variant === 'mobile'
        ? 'h-11 w-11 rounded-lg transition-colors flex items-center justify-center text-gray-500 hover:text-primary hover:bg-orange-50 border border-gray-200'
        : 'p-2.5 rounded transition-colors text-gray-500 hover:text-primary hover:bg-orange-50';

    const portalTarget =
      typeof document !== 'undefined' ? document.body : null;

    return (
      <div className="relative" data-jump-dropdown="true">
        <button
          onClick={(event) => {
            if (single) {
              single.action();
              return;
            }
            if (isOpen) {
              closeJumpDropdown();
            } else {
              setDropdownAnchorRect(
                event.currentTarget.getBoundingClientRect()
              );
              setOpenDropdownId(line.lineNumber);
            }
          }}
          className={buttonClass}
          title={tooltip}
          aria-label={tooltip}
          aria-haspopup={single ? undefined : 'menu'}
          aria-expanded={single ? undefined : isOpen}
        >
          <ExternalLink size={iconSize} />
        </button>
        {!single &&
          isOpen &&
          dropdownAnchorRect &&
          portalTarget &&
          createPortal(
            <div
              role="menu"
              data-jump-dropdown="true"
              className="fixed z-[60] bg-white border border-gray-200 rounded-lg shadow-lg min-w-[220px] py-1"
              style={{
                top: dropdownAnchorRect.bottom + 4,
                right: Math.max(
                  0,
                  window.innerWidth - dropdownAnchorRect.right
                ),
              }}
            >
              {targets.delivery && (
                <button
                  role="menuitem"
                  onClick={() => {
                    targets.delivery!.action();
                    closeJumpDropdown();
                  }}
                  className="w-full text-left px-3 py-2 text-sm font-mono hover:bg-orange-50 hover:text-primary"
                >
                  {targets.delivery.label}
                </button>
              )}
              {targets.order && (
                <button
                  role="menuitem"
                  onClick={() => {
                    targets.order!.action();
                    closeJumpDropdown();
                  }}
                  className="w-full text-left px-3 py-2 text-sm font-mono hover:bg-orange-50 hover:text-primary"
                >
                  {targets.order.label}
                </button>
              )}
            </div>,
            portalTarget
          )}
      </div>
    );
  };

  // Eine einzelne ARTICLE-`<tr>` rendern — wird sowohl im flachen Desktop-Pfad
  // (Einzelbeleg) als auch in jeder `DesktopDeliveryCard` (Sammelrechnung)
  // genutzt, damit Spalten- und Action-Layout identisch bleiben.
  const renderDesktopArticleRow = (line: DocumentLine): React.ReactNode => {
    const isFav = line.articleNumber
      ? favoriteArticleNumbers.has(line.articleNumber)
      : false;
    const isHighlighted =
      !!highlightArticleNumber &&
      line.articleNumber === highlightArticleNumber;
    return (
      <tr
        key={line.lineNumber}
        className={clsx(
          'group transition-colors',
          line.isReturn
            ? 'bg-red-50 hover:bg-red-100'
            : isHighlighted
              ? 'bg-yellow-50 hover:bg-yellow-100'
              : 'hover:bg-gray-50'
        )}
      >
        {selectionEnabled && (
          <td className="px-4 py-3 w-10">
            <Checkbox
              checked={selectionSet.has(line.lineNumber)}
              onClick={(e) => {
                e.stopPropagation();
                toggleLine(line);
              }}
            />
          </td>
        )}
        {/* align-top: Die Artikelnummer fluchtet mit dem Titel, der Hersteller
            mit der zweiten Beschreibungszeile — zwei ausgerichtete Spalten
            statt eines mittig schwebenden Blocks. Bei einzeiligen Positionen
            ändert sich dadurch nichts. */}
        <td className="px-4 py-3 align-top font-mono text-gray-700">
          <HighlightText text={line.articleNumber} />
          {line.manufacturer && (
            // Zweite Zeile kostet keine Höhe: die Beschreibung daneben ist
            // ohnehin zweizeilig. truncate + title hält die Zeilenhöhe auch
            // bei den langen Namen stabil ("Schellhorn Verbindungstechnik").
            <div
              className="text-xs font-sans text-gray-500 truncate"
              title={line.manufacturer}
            >
              <HighlightText text={line.manufacturer} />
            </div>
          )}
        </td>
        <td className="px-4 py-3 align-top">
          <p className="font-medium text-gray-900">
            {line.articleNumber ? (
              <ArticleTitleButton articleNumber={line.articleNumber}>
                <HighlightText text={line.description} />
              </ArticleTitleButton>
            ) : (
              <HighlightText text={line.description} />
            )}
          </p>
          {line.description2 && (
            <p className="text-sm text-gray-600">
              <HighlightText text={line.description2} />
            </p>
          )}
        </td>
        <td className="px-4 py-3 text-right whitespace-nowrap tabular-nums">
          <QuantityCell line={line} />
        </td>
        {showPrices && (
          <td className="px-4 py-3 text-right tabular-nums whitespace-nowrap">
            {formatCurrency(line.unitPrice ?? 0)}
            {line.priceFactor > 1 && (
              <span className="text-xs text-gray-500 ml-1">
                (per {line.priceFactor})
              </span>
            )}
          </td>
        )}
        {showPrices && (
          <td className="px-4 py-3 text-right tabular-nums text-red-600">
            {line.discount
              ? `-${line.discount.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`
              : '-'}
          </td>
        )}
        {showNettoPe && (
          <td
            className="px-4 py-3 text-right tabular-nums"
            title={isLegacyMissing ? legacyMissingTitle : undefined}
          >
            {isLegacyMissing || line.netUnitPrice == null
              ? '—'
              : formatCurrency(line.netUnitPrice)}
          </td>
        )}
        {showPrices && (
          <td className="px-4 py-3 text-right font-medium tabular-nums">
            {formatCurrency(line.amount ?? 0)}
          </td>
        )}
        <td className="px-4 py-3">
          <div className="flex items-center justify-center gap-2 transition-opacity">
            <button
              onClick={() => handleToggleFavorite(line.articleNumber)}
              className={clsx(
                'p-2.5 rounded transition-colors',
                isFav
                  ? 'text-primary bg-orange-50 hover:bg-orange-100'
                  : 'text-gray-500 hover:text-primary hover:bg-orange-50'
              )}
              title="Favorit"
            >
              <FavoriteIcon
                size={18}
                fill={isFav ? 'currentColor' : 'none'}
              />
            </button>
            {line.articleNumber && (
              <button
                onClick={() => addItem(line.articleNumber!, 1)}
                className={clsx(
                  'p-2.5 rounded transition-colors',
                  cartItems.some((ci) => ci.articleNumber === line.articleNumber)
                    ? 'text-primary bg-orange-50 hover:bg-orange-100'
                    : 'text-gray-500 hover:text-primary hover:bg-orange-50'
                )}
                title="Zur Anfrage"
              >
                <ClipboardList size={18} />
              </button>
            )}
            {line.articleNumber && (
              <button
                onClick={() => handleCopyArticleNumber(line.articleNumber)}
                className="p-2.5 rounded transition-colors text-gray-500 hover:text-primary hover:bg-orange-50"
                title="Artikelnummer kopieren"
              >
                <Copy size={18} />
              </button>
            )}
            {renderJumpButton(line, 'desktop')}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <>
      {/* Mobile Card Layout (< md) */}
      <div className="md:hidden space-y-3">
        {groupedItems.map((item) => {
          if (item.kind === 'text-group') {
            return (
              <div
                key={item.key}
                className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-sm text-gray-600 italic"
              >
                {item.texts.map((t, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && (
                      <span className="mx-2 text-gray-400 not-italic">·</span>
                    )}
                    {linkifyDocumentNumbers(t)}
                  </React.Fragment>
                ))}
              </div>
            );
          }

          const line = item.line;
          const isFav = line.articleNumber
            ? favoriteArticleNumbers.has(line.articleNumber)
            : false;

          return (
            <div
              key={line.lineNumber}
              className={clsx(
                'bg-white rounded-lg border border-gray-200 p-4 transition-colors',
                line.isReturn && 'bg-red-50 border-red-200'
              )}
            >
              {/* Top: Article name + number */}
              <ArticleNameBlock
                className="mb-3"
                articleNumber={line.articleNumber ?? ''}
                description={line.description}
                description2={line.description2}
                highlight
                linkArticle={!!line.articleNumber}
                descriptionClassName="font-medium text-gray-900 text-base"
                description2ClassName="text-sm text-gray-600"
                articleNumberClassName="text-sm text-gray-700 font-mono"
              />

              {/* Middle: Price + Quantity */}
              <div className="flex items-end justify-between mb-3 pb-3 border-b border-gray-100">
                {showPrices ? (
                  <>
                    <div>
                      <span className="text-lg font-bold tabular-nums text-gray-900">
                        {formatCurrency(line.amount ?? 0)}
                      </span>
                      {line.discount ? (
                        <span className="text-sm text-red-600 ml-2 tabular-nums">
                          -{line.discount.toLocaleString('de-DE', { maximumFractionDigits: 1 })}&thinsp;% Rabatt
                        </span>
                      ) : null}
                    </div>
                    <div className="text-right">
                      <QuantityCell
                        line={line}
                        className="text-sm font-medium text-gray-700 tabular-nums"
                      />
                      <p className="text-sm text-gray-600 tabular-nums">
                        à {formatCurrency(line.unitPrice ?? 0)}
                        {line.priceFactor > 1 && (
                          <span className="text-xs text-gray-500 ml-1">
                            (per {line.priceFactor})
                          </span>
                        )}
                      </p>
                      {showNettoPe && (
                        <p
                          className="text-sm text-gray-600 tabular-nums"
                          title={isLegacyMissing ? legacyMissingTitle : undefined}
                        >
                          NettoPE{' '}
                          {isLegacyMissing || line.netUnitPrice == null
                            ? '—'
                            : formatCurrency(line.netUnitPrice)}
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <QuantityCell
                    line={line}
                    className="text-base font-medium text-gray-900 tabular-nums"
                  />
                )}
              </div>

              {/* Bottom: Actions */}
              <div className="flex items-center gap-2">
                {line.articleNumber && (
                  <button
                    onClick={() => addItem(line.articleNumber!, 1)}
                    className={clsx(
                      'flex-1 h-11 rounded-lg transition-colors flex items-center justify-center gap-2 font-bold text-sm',
                      cartItems.some((ci) => ci.articleNumber === line.articleNumber)
                        ? 'bg-orange-50 text-primary border border-primary hover:bg-orange-100'
                        : 'bg-primary hover:bg-primary-hover text-white'
                    )}
                  >
                    {cartItems.some((ci) => ci.articleNumber === line.articleNumber) ? (
                      <>
                        <Check size={20} />
                        Auf Liste
                      </>
                    ) : (
                      <>
                        <ClipboardList size={20} />
                        Zur Anfrage
                      </>
                    )}
                  </button>
                )}
                {renderJumpButton(line, 'mobile')}
                <button
                  onClick={() => handleToggleFavorite(line.articleNumber)}
                  className={clsx(
                    'h-11 w-11 rounded-lg transition-colors flex items-center justify-center',
                    isFav
                      ? 'text-primary bg-orange-50 hover:bg-orange-100'
                      : 'text-gray-500 hover:text-primary hover:bg-orange-50 border border-gray-200'
                  )}
                  title="Favorit"
                >
                  <FavoriteIcon
                    size={20}
                    fill={isFav ? 'currentColor' : 'none'}
                  />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop Layout (>= md): Positions-Suche + bei Sammelrechnungen pro
          Lieferung eine eigene Card; bei Einzelbelegen oder aktiver Suche eine
          flache Tabelle (inkl. text-group-Rendering fuer eingestreute Hinweise). */}
      <div className="hidden md:block space-y-3">
        {articleLines.length >= 5 && (
          <div className="flex justify-end">
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg pl-2.5 pr-1 h-9 w-full max-w-[280px]">
              <Search size={15} className="text-gray-400 shrink-0" />
              <input
                type="text"
                placeholder="In Positionen suchen…"
                value={positionSearch}
                onChange={(e) => setPositionSearch(e.target.value)}
                className="flex-1 bg-transparent text-sm outline-none min-w-0"
              />
              {positionSearch && (
                <button
                  type="button"
                  onClick={() => setPositionSearch('')}
                  className="w-6 h-6 grid place-items-center text-gray-400 hover:text-gray-600"
                  aria-label="Suche leeren"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        )}

        {hasGrouping && !isSearching ? (
          <div className="space-y-3">
            {buckets.map((bucket) => (
              <DesktopDeliveryCard
                key={bucket.deliveryNumber ?? '__default__'}
                bucket={bucket}
                renderArticleRow={renderDesktopArticleRow}
                showPrices={showPrices}
                showNettoPe={showNettoPe}
                isLegacyMissing={isLegacyMissing}
                legacyMissingTitle={legacyMissingTitle}
                onJumpToOrder={navigateToReferencedDocument}
                existingOrderNumbers={existingOrderNumbers}
                selectionEnabled={selectionEnabled}
                selected={selectionSet}
                onToggleBucket={toggleBucket}
              />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-600 font-medium border-b border-gray-200">
                <tr>
                  {selectionEnabled && (
                    <th className="px-4 py-3 w-10">
                      <Checkbox
                        checked={
                          displayedArticles.length > 0 &&
                          displayedArticles.every((l) =>
                            selectionSet.has(l.lineNumber)
                          )
                        }
                        onClick={() =>
                          setLinesSelected(
                            displayedArticles,
                            !displayedArticles.every((l) =>
                              selectionSet.has(l.lineNumber)
                            )
                          )
                        }
                      />
                    </th>
                  )}
                  <th className="px-4 py-3 w-40">Art.-Nr.</th>
                  <th className="px-4 py-3">Beschreibung</th>
                  <th className="px-4 py-3 text-right">Menge</th>
                  {showPrices && <th className="px-4 py-3 text-right">Einzelpreis</th>}
                  {showPrices && <th className="px-4 py-3 text-right">Rabatt</th>}
                  {showNettoPe && (
                    <th
                      className="px-4 py-3 text-right"
                      title={isLegacyMissing ? legacyMissingTitle : undefined}
                    >
                      NettoPE
                    </th>
                  )}
                  {showPrices && <th className="px-4 py-3 text-right">Gesamt</th>}
                  <th className="px-4 py-3 text-center w-48">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {filteredArticles ? (
                  filteredArticles.length === 0 ? (
                    <tr>
                      <td
                        colSpan={desktopColCount}
                        className="px-4 py-8 text-center text-sm text-gray-500"
                      >
                        Keine Positionen gefunden.
                      </td>
                    </tr>
                  ) : (
                    filteredArticles.map((line) => renderDesktopArticleRow(line))
                  )
                ) : (
                  groupedItems.map((item) => {
                    if (item.kind === 'text-group') {
                      return (
                        <tr key={item.key} className="bg-gray-50">
                          <td
                            colSpan={desktopColCount}
                            className="px-4 py-2 text-sm text-gray-600 italic"
                          >
                            {item.texts.map((t, i) => (
                              <React.Fragment key={i}>
                                {i > 0 && (
                                  <span className="mx-2 text-gray-400 not-italic">·</span>
                                )}
                                {linkifyDocumentNumbers(t)}
                              </React.Fragment>
                            ))}
                          </td>
                        </tr>
                      );
                    }
                    return renderDesktopArticleRow(item.line);
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </>
  );
}
